import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import {
  Cell,
  CellProps,
  Column,
  ColumnInstance,
  HeaderGroup,
  Hooks,
  Row,
  SortingRule,
  TableCellProps,
  TableHeaderProps,
  TableState,
  useFlexLayout,
  usePagination,
  UsePaginationInstanceProps,
  UsePaginationState,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { ButtonGroup, Icon, Table as USWDSTable, USWDSIcons } from '@ver-uds/uswds-react';
import innerText from 'react-innertext';
import Button from '../Button';
import { Checkbox } from '../FormControls';
import { PaginationProps } from '../Pagination/Pagination';
import Floating from './Floating';
import PageLoader from '../PageLoader';

/* eslint-disable @typescript-eslint/no-empty-interface, @typescript-eslint/no-explicit-any */
/**
 * Custom configuration for react-table based on available plugins.
 * This configuration uses declaration merging.
 *
 * @TODO Find a better place for this declaration. The type overrides need to be present in the compiled type declarations.
 * @see https://github.com/DefinitelyTyped/DefinitelyTyped/tree/master/types/react-table#configuration-using-declaration-merging
 */

interface TableSelectionOptions {
  // Optional param to enable access to selected row data
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateSelectedRowData?: Function;
}
declare module 'react-table' {
  export interface TableOptions<D>
    extends UseRowSelectOptions<D>,
      UseSortByOptions<D>,
      UsePaginationOptions<D>,
      TableSelectionOptions {}

  export interface Hooks<D> extends UseRowSelectHooks<D>, UseSortByHooks<D> {}

  export interface TableInstance<D>
    extends UseRowSelectInstanceProps<D>,
      UseSortByInstanceProps<D>,
      UsePaginationInstanceProps<D> {}

  export interface TableState<D> extends UseRowSelectState<D>, UseSortByState<D>, UsePaginationState<D> {}

  export interface ColumnInterface<D> extends UseSortByColumnOptions<D> {
    isRowHeader?: boolean;
    Footer?: (() => JSX.Element) | string;
  }

  export interface ColumnInstance<D> extends UseSortByColumnProps<D> {}

  export interface Cell {}

  export interface Row<D> extends UseRowSelectRowProps<D> {
    values: D;
  }
}
/* eslint-enable */

interface SortIconProps {
  sorted: boolean;
  sortedDesc: boolean;
}

const SortIcon = ({ sorted, sortedDesc }: SortIconProps): JSX.Element => {
  if (sorted) {
    return <Icon name={sortedDesc ? 'arrow_downward' : 'arrow_upward'} />;
  }
  return <Icon name="sort_arrow" />;
};
const selectionId = 'selection';
const rowActionsId = 'actions';
// checkbox width 22 + left padding 16 + right padding 16
const rowSelectionWidth = 54;
// Icon width 24
const rowActionIconWidth = 24;
// Actions left padding 16 right padding 16
const rowActionPadding = 32;
// Action Icon left margin 6 + right margin 6
const rowActionMargin = 12;

function rowActionsWidth<D extends Record<string, unknown>>(rowActions: RowAction<D>[]): number {
  const buttonWidth = rowActionIconWidth * rowActions.length;
  // Outer margins are removed so there is one fewer margin than the number of icons.
  // [Icon]<- margin ->[Icon]<- margin ->[Icon]
  const buttonInnerMargins = (rowActions.length - 1) * rowActionMargin;
  return buttonWidth + buttonInnerMargins + rowActionPadding;
}

type FloatOptions = {
  floatRowActions: boolean;
  floatRowHeaders: boolean;
  floatRowSelect: boolean;
};

function isCell<D extends Record<string, unknown>>(cell: Cell<D> | HeaderGroup<D>): cell is Cell<D> {
  return 'getCellProps' in cell;
}

function calculateRowHeadersWidth<D extends Record<string, unknown>>(
  cells: Array<Cell<D> | HeaderGroup<D>>,
  { floatRowActions, floatRowHeaders, floatRowSelect }: FloatOptions,
) {
  return cells.reduce((width, cell) => {
    const headerCell: ColumnInstance<D> | HeaderGroup<D> = isCell(cell) ? cell.column : cell;
    const cellProps: TableCellProps | TableHeaderProps = isCell(cell) ? cell.getCellProps() : cell.getHeaderProps();

    if (
      (floatRowHeaders && headerCell.isRowHeader) ||
      (floatRowActions && headerCell.id === rowActionsId) ||
      (floatRowSelect && headerCell.id === selectionId)
    ) {
      return width + parseFloat(String(cellProps.style?.width));
    }
    return width;
  }, 0);
}

function calculateRowHeaderStyles<D extends Record<string, unknown>>(
  cells: Cell<D>[] | HeaderGroup<D>[],
  cellIndex: number,
  float: FloatOptions,
): React.CSSProperties {
  return {
    left: calculateRowHeadersWidth(cells.slice(0, cellIndex), float),
    right: calculateRowHeadersWidth(cells.slice(cellIndex + 1), float),
  };
}

function calculateLeftFloatRefStyles<D extends Record<string, unknown>>(
  cells: Cell<D>[] | HeaderGroup<D>[],
  cellIndex: number,
  float: FloatOptions,
): React.CSSProperties {
  return {
    transform: `translate(-${calculateRowHeadersWidth(cells.slice(0, cellIndex), float)}px)`,
  };
}

function calculateRightFloatRefStyles<D extends Record<string, unknown>>(
  cells: Cell<D>[] | HeaderGroup<D>[],
  cellIndex: number,
  float: FloatOptions,
): React.CSSProperties {
  return {
    transform: `translate(${calculateRowHeadersWidth(cells.slice(cellIndex + 1), float)}px)`,
  };
}

export interface RowAction<D extends Record<string, unknown>> {
  handler(row: Row<D>): void;
  icon: USWDSIcons;
  label: string | ((row: Row<D>) => string);
}

interface ManualPaginationOptions {
  onPageChange: ({ pageIndex, pageSize }: { pageIndex: number; pageSize: number }) => void;
  rowCount: number;
  pageCount: number;
}

interface Pagination<D extends Record<string, unknown>> {
  render(
    dataSize: number,
    object: UsePaginationInstanceProps<D> & UsePaginationState<D>,
  ): React.ReactElement<PaginationProps>;
  manual?: ManualPaginationOptions;
  isLoading?: boolean;
}

export interface TableProps<D extends Record<string, unknown>> {
  id: string;
  friendlyTableName?: string;
  variant?: 'default' | 'small' | 'large';
  columns: Column<D>[];
  data: D[];
  sortable?: boolean;
  manualSort?: boolean;
  selectable?: boolean;
  rowActions?: RowAction<D>[];
  caption?: string;
  initialState?: Partial<TableState<D>>;
  pagination?: Pagination<D>;
  disableOverflowXScroll?: boolean;
  onSortChange?: (sortBy: SortingRule<D>[]) => void;
  autoResetPage?: boolean;
  floatRowActions?: boolean;
  floatRowHeaders?: boolean;
  floatTableHeaders?: boolean;
  floatRowSelect?: boolean;
  maxHeight?: string | number;
  // eslint-disable-next-line @typescript-eslint/ban-types
  updateSelectedRowData?: Function;
}

const Table = <D extends Record<string, unknown>>({
  id,
  friendlyTableName,
  variant = 'default',
  columns,
  data,
  sortable = false,
  manualSort = false,
  selectable = false,
  rowActions,
  caption,
  initialState,
  pagination,
  disableOverflowXScroll = false,
  onSortChange,
  autoResetPage = true,
  floatRowActions = false,
  floatRowHeaders = false,
  floatTableHeaders = false,
  floatRowSelect = false,
  maxHeight: tableheight,
  updateSelectedRowData,
}: TableProps<D>): JSX.Element => {
  const floatOptions: FloatOptions = { floatRowActions, floatRowHeaders, floatRowSelect };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    pageOptions,
    page,
    pageCount,
    state: { pageIndex, pageSize, sortBy },
    gotoPage,
    previousPage,
    nextPage,
    setPageSize,
    canPreviousPage,
    canNextPage,
    selectedFlatRows,
    toggleAllRowsSelected,
  } = useTable(
    {
      columns,
      data,
      autoResetPage,
      initialState,
      manualSortBy: manualSort,
      ...(pagination?.manual !== undefined && {
        manualPagination: true,
        pageCount: pagination.manual?.pageCount,
      }),
      updateSelectedRowData,
    },
    useFlexLayout,
    ...(sortable ? [useSortBy] : []),
    ...(pagination ? [usePagination] : []),
    ...(selectable
      ? [
          useRowSelect,
          (hooks: Hooks<D>): void => {
            // Add a new first column with checkboxes to select rows.
            hooks.visibleColumns.push((visibleColumns) => [
              {
                id: selectionId,
                width: rowSelectionWidth,
                Header: ({ getToggleAllRowsSelectedProps, isAllRowsSelected }): JSX.Element => {
                  return (
                    <Checkbox
                      id={`${id}-select-all`}
                      name={`${id}-select-all`}
                      label={isAllRowsSelected ? 'Deselect all' : 'Select all'}
                      screenReaderOnlyLabel
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...getToggleAllRowsSelectedProps({
                        title: undefined,
                      })}
                    />
                  );
                },
                Cell: ({
                  row: { index, id: rowId, getToggleRowSelectedProps, isSelected },
                }: CellProps<D>): JSX.Element => {
                  const displayId = rowId !== String(index) ? rowId : index + 1;

                  return (
                    <Checkbox
                      id={`${id}-select-row-${rowId}`}
                      name={`${id}-select-row-${rowId}`}
                      label={isSelected ? `Deselect row ${displayId}` : `Select row ${displayId}`}
                      screenReaderOnlyLabel
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...getToggleRowSelectedProps({
                        title: undefined,
                      })}
                    />
                  );
                },
              },
              ...visibleColumns,
            ]);
          },
        ]
      : []),
    ...(rowActions
      ? [
          (hooks: Hooks<D>): void => {
            // Add a new last column with row actions.
            hooks.visibleColumns.push((visibleColumns) => [
              ...visibleColumns,
              {
                id: rowActionsId,
                width: rowActionsWidth(rowActions),
                Header: () => <span className="usa-sr-only">Actions</span>,
                Cell: ({ row }: CellProps<D>): JSX.Element => (
                  <ButtonGroup>
                    {rowActions.map(({ handler, icon, label }) => (
                      <Button
                        key={typeof label === 'function' ? label(row) : label}
                        type="button"
                        variant="tertiary"
                        onClick={(): void => handler(row)}
                        aria-label={typeof label === 'function' ? label(row) : label}
                      >
                        <Icon name={icon} />
                      </Button>
                    ))}
                  </ButtonGroup>
                ),
              },
            ]);
          },
        ]
      : []),
  );

  const [tableTabIndex, setTableTabIndex] = useState(!disableOverflowXScroll ? 0 : undefined);

  useEffect(() => {
    pagination?.manual?.onPageChange({ pageIndex, pageSize });
  }, [pagination?.manual, pageIndex, pageSize]);

  useEffect(() => {
    if (typeof onSortChange === 'function') {
      onSortChange(sortBy);
    }
  }, [manualSort, onSortChange, sortBy]);

  useEffect(() => {
    // Enables the selected row data to be visible to parent component
    if (selectedFlatRows && updateSelectedRowData) {
      updateSelectedRowData(selectedFlatRows.map((row) => row.original));
    }
  }, [selectedFlatRows, updateSelectedRowData]);

  useEffect(() => {
    //  Unselects all rows upon column sort
    if (selectable && sortable) {
      toggleAllRowsSelected(false);
    }
  }, [selectable, sortBy, sortable, toggleAllRowsSelected]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const hasFooter = columns.some((col) => 'Footer' in col);

  useEffect(() => {
    if (scrollContainerRef && scrollContainerRef.current) {
      new ResizeObserver(() => {
        // table scroll container only needs focus when content is overflowing
        const contentOverflowing =
          (scrollContainerRef?.current?.scrollWidth ?? 0) > (scrollContainerRef?.current?.clientWidth ?? 0);
        const newTableTabIndex = !disableOverflowXScroll && contentOverflowing ? 0 : undefined;
        setTableTabIndex(newTableTabIndex);
      }).observe(scrollContainerRef.current);
    }
  }, [scrollContainerRef, disableOverflowXScroll]);

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <div className={PageLoader.sectionParentClassname}>
      <div
        className={classNames({
          'overflow-x-auto': !disableOverflowXScroll,
        })}
        // Ensure users navigating with a keyboard are able to select and scroll the table
        // https://designsystem.digital.gov/components/table/#accessibility-table
        //
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={tableTabIndex}
        style={{ maxHeight: tableheight }}
        ref={scrollContainerRef}
      >
        <USWDSTable
          borderless
          {...getTableProps((tableProps) => ({
            ...tableProps,
            // TODO: Should `aria-multiselectable="true"` be set if the table is selectable? What about `role="grid"`?
            // https://www.w3.org/TR/wai-aria-1.2/#aria-multiselectable
            // https://www.w3.org/TR/wai-aria-practices-1.2/#grid
            className: classNames(tableProps.className, 'ver-table', {
              'ver-table--small': variant === 'small',
              'ver-table--large': variant === 'large',
              'ver-table--sortable': sortable,
              'ver-table--selectable': selectable,
            }),
          }))}
        >
          {caption ? <caption>{caption}</caption> : null}
          <Floating active={floatTableHeaders} containerRef={scrollContainerRef}>
            {(rowFloating, _leftRef, _rightRef, topRef) => (
              <>
                {floatTableHeaders && (
                  <div ref={topRef} className="ver-table__float-reference" style={{ display: 'table-caption' }} />
                )}
                <thead
                  className={classNames({
                    'ver-table__head--float': floatTableHeaders,
                    'ver-table__head--floating': rowFloating,
                  })}
                >
                  {headerGroups.map((headerGroup) => (
                    <tr
                      {...headerGroup.getHeaderGroupProps((headerGroupProps, { instance }) => ({
                        ...headerGroupProps,
                        className: classNames(headerGroupProps.className, 'ver-table__header-group', {
                          'ver-table__header-group--selected': instance.isAllRowsSelected,
                        }),
                      }))}
                    >
                      {headerGroup.headers.map((column, cellIndex, headers) => {
                        const { isRowHeader, id: columnId } = column;
                        const isRowActions = floatRowActions && columnId === rowActionsId;
                        const isSelection = floatRowSelect && columnId === selectionId;
                        const isFloatingRowHeader = floatRowHeaders && isRowHeader;
                        const isFloatingEnabled = isFloatingRowHeader || isRowActions || isSelection;
                        return (
                          <Floating
                            containerRef={scrollContainerRef}
                            active={isFloatingEnabled}
                            key={`${column.getHeaderProps().key}-floating`}
                          >
                            {(floating, leftRef, rightRef) => {
                              return (
                                <>
                                  {isFloatingEnabled && (
                                    <div
                                      className="ver-table__float-reference"
                                      ref={leftRef}
                                      style={calculateLeftFloatRefStyles(headers, cellIndex, floatOptions)}
                                    />
                                  )}
                                  <th
                                    {...column.getHeaderProps((headerProps) => {
                                      return {
                                        ...headerProps,
                                        ...(column.canSort && {
                                          ...column.getSortByToggleProps({
                                            style: {
                                              ...headerProps.style,
                                              cursor: undefined,
                                            },
                                            title: undefined,
                                            onClick: undefined,
                                            ...(column.isSorted
                                              ? {
                                                  'aria-sort': column.isSortedDesc ? 'descending' : 'ascending',
                                                }
                                              : { 'aria-sort': 'none' }),
                                          }),
                                        }),
                                        style: {
                                          ...headerProps.style,
                                          ...(isFloatingEnabled &&
                                            calculateRowHeaderStyles(headers, cellIndex, floatOptions)),
                                          ...(isRowActions && { left: 0 }),
                                        },
                                        className: classNames(
                                          headerProps.className,
                                          column.canSort &&
                                            (column.getSortByToggleProps() as { className?: string }).className,
                                          'ver-table__header',
                                          {
                                            'ver-table__header--sortable': column.canSort,
                                            'ver-table__header--sorted': column.isSorted,
                                            'ver-table__row-header': isRowHeader || isSelection,
                                            'ver-table__row-actions': isRowActions,
                                            'ver-table__row-header--floating': floating,
                                            'ver-table__header--floating': rowFloating,
                                          },
                                        ),
                                        id: `header-${id}-${column.id}`,
                                      };
                                    })}
                                    scope="col"
                                  >
                                    {column.canSort ? (
                                      <Button
                                        type="button"
                                        variant="tertiary"
                                        className="ver-table__sort-button"
                                        title={`Click to sort ${
                                          friendlyTableName ? `${friendlyTableName} ` : ''
                                        }by ${innerText(column.render('Header'))}`}
                                        aria-describedby={`header-${id}-${column.id}-describedby`}
                                        onClick={(event): void => {
                                          event.persist();
                                          column.toggleSortBy((undefined as unknown) as boolean, false);
                                        }}
                                      >
                                        {column.render('Header')}
                                        {column.isSorted && (
                                          <span
                                            style={{
                                              // Using inline style to ensure the span is not visually visible
                                              border: '0px solid',
                                              clip: 'rect(0px, 0px, 0px, 0px)',
                                              height: '1px',
                                              width: '1px',
                                              margin: '-1px',
                                              padding: '0px',
                                              whiteSpace: 'nowrap',
                                              position: 'absolute',
                                            }}
                                          >
                                            {` sorted ${column.isSortedDesc ? 'descending' : 'ascending'}.`}
                                          </span>
                                        )}
                                        <span className="ver-table__sort-indicator">
                                          <SortIcon
                                            sorted={column.isSorted}
                                            sortedDesc={Boolean(column.isSortedDesc)}
                                          />
                                        </span>
                                        <p id={`header-${id}-${column.id}-describedby`} hidden>
                                          {`Click to sort ${
                                            friendlyTableName ? `${friendlyTableName} ` : ''
                                          }by ${innerText(column.render('Header'))}`}
                                        </p>
                                      </Button>
                                    ) : (
                                      column.render('Header')
                                    )}
                                  </th>
                                  {isFloatingEnabled && (
                                    <div
                                      className="ver-table__float-reference"
                                      ref={rightRef}
                                      style={calculateRightFloatRefStyles(headers, cellIndex, floatOptions)}
                                    />
                                  )}
                                </>
                              );
                            }}
                          </Floating>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
              </>
            )}
          </Floating>
          <tbody {...getTableBodyProps()}>
            {(pagination ? page : rows).map((row) => {
              prepareRow(row);

              return (
                <tr
                  {...row.getRowProps((rowProps, { row: rowInstance }) => ({
                    ...rowProps,
                    'aria-selected': selectable ? String(rowInstance.isSelected) : undefined,
                    className: classNames(rowProps.className, 'ver-table__row', {
                      'ver-table__row--selected': rowInstance.isSelected,
                    }),
                  }))}
                >
                  {row.cells.map((cell, cellIndex, cells) => {
                    const { isRowHeader, id: columnId } = cell.column;
                    const TableCell = isRowHeader ? 'th' : 'td';
                    const isRowActions = floatRowActions && columnId === rowActionsId;
                    const isSelection = floatRowSelect && columnId === selectionId;
                    const isFloatingRowHeader = floatRowHeaders && isRowHeader;
                    const isFloatingEnabled = isFloatingRowHeader || isRowActions || isSelection;
                    return (
                      <Floating
                        containerRef={scrollContainerRef}
                        active={isFloatingEnabled}
                        key={`${cell.getCellProps().key}-floating`}
                      >
                        {(floating, leftRef, rightRef) => (
                          <>
                            {isFloatingEnabled && (
                              <div
                                className="ver-table__float-reference"
                                ref={leftRef}
                                style={calculateLeftFloatRefStyles(cells, cellIndex, floatOptions)}
                              />
                            )}
                            <TableCell
                              {...cell.getCellProps((cellProps) => ({
                                ...cellProps,
                                ...(isRowHeader && { role: 'rowheader', scope: 'row' }),
                                style: {
                                  ...cellProps.style,
                                  ...(isFloatingEnabled && calculateRowHeaderStyles(cells, cellIndex, floatOptions)),
                                  ...(columnId === rowActionsId && { left: 0 }),
                                },
                                className: classNames({
                                  'ver-table__row-header': isRowHeader || isSelection,
                                  'ver-table__row-actions': columnId === rowActionsId,
                                  'ver-table__row-header--floating': floating,
                                }),
                              }))}
                            >
                              {cell.render('Cell')}
                            </TableCell>
                            {isFloatingEnabled && (
                              <div
                                className="ver-table__float-reference"
                                ref={rightRef}
                                style={calculateRightFloatRefStyles(cells, cellIndex, floatOptions)}
                              />
                            )}
                          </>
                        )}
                      </Floating>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          {hasFooter && (
            <tfoot>
              {footerGroups.map((group) => (
                <tr {...group.getFooterGroupProps()}>
                  {group.headers.map((column) => (
                    <td {...column.getFooterProps()}>{column.render('Footer')}</td>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </USWDSTable>
        {pagination?.isLoading ? <PageLoader label="Loading…" sectionOverlay ariaLive="polite" /> : null}
      </div>
      {pagination &&
        pagination.render(pagination?.manual?.rowCount ?? data.length, {
          page,
          pageCount,
          pageOptions,
          gotoPage,
          previousPage,
          nextPage,
          setPageSize,
          canPreviousPage,
          canNextPage,
          pageIndex,
          pageSize,
        })}
    </div>
  );
  /* eslint-enable react/jsx-props-no-spreading */
};

export default Table;
